import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { theme } from 'app/theme/index'
import { Link } from 'gatsby'
import React, { memo } from 'react'
import { ArrowRightCircle } from 'react-feather'

export interface Props {
  iconArrowRight?: boolean
  iconReceptionBell?: boolean
  label: string
  file?: string
  target?: string
  URL?: string
  onClick?: () => void
  className?: string
  noActions?: boolean
  rel?: string
  variant?: Variant
}

export const Button = memo(function Button({
  iconArrowRight,
  iconReceptionBell,
  label = 'Button',
  file,
  target,
  URL,
  onClick,
  className,
  noActions,
  rel,
  variant = 'M',
}: Props) {
  const buttonClassName = `${className ? className : ''}`
  const buttonDOM = (
    <>
      {iconReceptionBell ? (
        <svg
          className="icon-reception-bell"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="23.171"
          viewBox="0 0 20 23.171"
        >
          <path
            d="M11652.176-1548.616H11632.8a.555.555,0,0,1,0-1.109h.039c.893-.033,1.792,0,2.68,0h19.38a.555.555,0,0,1,0,1.109h-.039c-.3.011-.6.015-.895.015C11653.365-1548.6,11652.768-1548.616,11652.176-1548.616Zm-.865-3.227h-17.388a.506.506,0,0,1-.437-.22.511.511,0,0,1-.106-.335,10.552,10.552,0,0,1,2.432-6.7,10.534,10.534,0,0,1,7.4-3.729v-4.664h-2.675a.555.555,0,0,1,0-1.109H11647a.555.555,0,0,1,0,1.109h-2.68v4.649a10.168,10.168,0,0,1,3.1.607,10.637,10.637,0,0,1,4.984,3.8,10.436,10.436,0,0,1,1.926,6.043.509.509,0,0,1-.191.416.518.518,0,0,1-.369.138h-.033c-.269.011-.537.015-.807.015C11652.386-1551.827,11651.847-1551.842,11651.311-1551.842Zm-14.935-1.109h16.82a9.313,9.313,0,0,0-2.113-5.389,9.479,9.479,0,0,0-4.673-3.054,9.476,9.476,0,0,0-5.827.227,9.439,9.439,0,0,0-6.079,8.2h.264C11635.3-1552.965,11635.842-1552.951,11636.376-1552.951Z"
            transform="translate(1568.601 11655.433) rotate(-90)"
          />
        </svg>
      ) : null}
      <ButtonLabel>{label}</ButtonLabel>
      {iconArrowRight ? <ArrowRightCircle /> : null}
    </>
  )
  const EXTERNAL_URL_REGEX = new RegExp('^(https?|ftp|www)://')
  const externalURL = URL && EXTERNAL_URL_REGEX.test(URL)

  return (
    <>
      {file ? (
        <Anchor
          className={buttonClassName}
          href={file}
          target="_blank"
          rel={rel}
          variant={variant}
        >
          {buttonDOM}
        </Anchor>
      ) : (
        <>
          {!externalURL ? (
            onClick != undefined ? (
              <Anchor
                className={buttonClassName}
                onClick={onClick}
                variant={variant}
              >
                {buttonDOM}
              </Anchor>
            ) : noActions ? (
              <Static className={buttonClassName} variant={variant}>
                {buttonDOM}
              </Static>
            ) : (
              <ButtonItem
                to={URL ? URL : '#'}
                className={buttonClassName}
                variant={variant}
              >
                {buttonDOM}
              </ButtonItem>
            )
          ) : (
            <Anchor
              className={buttonClassName}
              href={URL}
              rel="noopener"
              target={target}
              variant={variant}
            >
              {buttonDOM}
            </Anchor>
          )}
        </>
      )}
    </>
  )
})

const SmallStyle = css`
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.variants.neutralDark1};
  font-family: ${theme.fontFamily.heading};
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: 0.0281rem;
  line-height: 1.125rem;
  margin-top: 2.5rem;
  transition: 0.3s ease-in-out;
  &:hover {
    letter-spacing: 0.075rem;
    svg {
      transform: translateX(-1.125rem);
    }
  }
  svg {
    width: auto;
    height: 3.75rem;
    stroke: ${theme.colors.variants.neutralLight3};
    stroke-width: 0.6;
    transform: translateX(-1.4375rem);
    transition: 0.3s ease-in-out;
  }

  @media (max-width: 1199px) {
    margin-top: 2.25rem;
  }
`

const MediumStyle = css`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  background: ${theme.colors.variants.neutralDark1};
  color: ${theme.colors.variants.neutralLight4};
  font-family: ${theme.fontFamily.heading};
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: 0.0938rem;
  line-height: 1.125rem;
  overflow: hidden;
  margin-top: 3rem;
  padding: 1.3125rem 1.875rem;
  position: relative;
  &:hover {
    &:after {
      top: 0;
    }
  }
  &:after {
    content: '';
    width: 100%;
    height: 100%;
    background: ${theme.colors.variants.primaryLight1};
    position: absolute;
    top: 100%;
    left: 0;
    transition: 0.3s ease-out;
  }
  svg {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 1199px) {
    margin-top: 2.25rem;
  }
`

const LargeStyle = css`
  cursor: pointer;
  display: inline-flex;
  height: 4.375rem;
  align-items: center;
  background: ${theme.colors.variants.primaryLight1};
  color: ${theme.colors.variants.neutralLight4};
  font-family: ${theme.fontFamily.heading};
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: 0.0187rem;
  line-height: 1.125rem;
  overflow: hidden;
  padding: 0 2.25rem;
  position: relative;
  &:hover {
    &:after {
      top: 0;
    }
  }
  &:after {
    content: '';
    width: 100%;
    height: 100%;
    background: ${theme.colors.variants.neutralDark1};
    position: absolute;
    top: 100%;
    left: 0;
    transition: 0.3s ease-out;
  }
  svg {
    position: relative;
    z-index: 2;
  }
`

const Anchor = styled.a<ContainerProps>`
  .icon-reception-bell {
    fill: ${({ theme }) => theme.colors.variants.neutralLight4};
    margin-right: 1rem;
    transform: rotate(90deg);
  }
  ${({ variant }) => {
    switch (variant) {
      case 'S':
        return SmallStyle
      case 'M':
        return MediumStyle
      case 'L':
        return LargeStyle
    }
  }}
`

const Static = styled.div<ContainerProps>`
  .icon-reception-bell {
    fill: ${({ theme }) => theme.colors.variants.neutralLight4};
    margin-right: 1rem;
    transform: rotate(90deg);
  }
  ${({ variant }) => {
    switch (variant) {
      case 'S':
        return SmallStyle
      case 'M':
        return MediumStyle
      case 'L':
        return LargeStyle
    }
  }}
`

const ButtonItem = styled(Link)<ContainerProps>`
  .icon-reception-bell {
    fill: ${({ theme }) => theme.colors.variants.neutralLight4};
    margin-right: 1rem;
    transform: rotate(90deg);
  }
  ${({ variant }) => {
    switch (variant) {
      case 'S':
        return SmallStyle
      case 'M':
        return MediumStyle
      case 'L':
        return LargeStyle
    }
  }}
`

const ButtonLabel = styled.span`
  position: relative;
  z-index: 2;
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'S' | 'M' | 'L'
